import React, { createContext, useContext, useMemo, useEffect, useState, useCallback } from 'react'
import io from 'socket.io-client'
import Cookies from 'js-cookie'

const baseDomain = window.location.hostname === 'localhost'
  ? process.env.REACT_APP_LOCAL_API
  : window.location.origin

const AdminSocketContext = createContext(null)

export const AdminSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(null)

  const socket = useMemo(
    () =>
      io(baseDomain, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 5000,
        transports: ['websocket', 'polling'],
        auth: (cb) => {
          const token = Cookies.get('auth_token') || ''
          cb({ token })
        },
      }),
    []
  )

  useEffect(() => {
    const onConnect = () => {
      setConnected(true)
      setConnectionError(null)
    }
    const onDisconnect = () => setConnected(false)
    const onConnectError = (err) => setConnectionError(err.message)
    const onError = (data) => {
      if (data?.error) setConnectionError(data.error)
    }

    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    socket.on('connect_error', onConnectError)
    socket.on('error-message', onError)

    return () => {
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      socket.off('connect_error', onConnectError)
      socket.off('error-message', onError)
    }
  }, [socket])

  const value = useMemo(() => ({ socket, connected, connectionError }), [socket, connected, connectionError])

  return (
    <AdminSocketContext.Provider value={value}>
      {children}
    </AdminSocketContext.Provider>
  )
}

export const useAdminSocket = () => {
  const ctx = useContext(AdminSocketContext)
  if (!ctx) throw new Error('useAdminSocket must be used within AdminSocketProvider')
  return ctx
}

export default AdminSocketContext
